<template>
  <div class="tasks-page">
    <header class="tasks-header">
      <div>
        <h2 class="page-title">我的任务</h2>
        <p class="page-subtitle">显示当前需要完成的培训资料与阶段考试</p>
      </div>
      <el-button
        size="small"
        type="primary"
        plain
        :loading="loading"
        @click="loadTasks"
      >
        刷新任务
      </el-button>
    </header>

    <section class="task-stats">
      <div class="stat-item actionable">
        <div class="stat-label">可立即完成</div>
        <div class="stat-value">{{ unlockedTaskCount }}</div>
      </div>
      <div class="stat-item locked">
        <div class="stat-label">待解锁</div>
        <div class="stat-value">{{ lockedTaskCount }}</div>
      </div>
      <div class="stat-item total">
        <div class="stat-label">任务总数</div>
        <div class="stat-value">{{ taskList.length }}</div>
      </div>
    </section>

    <section
      class="task-layout"
      v-loading="loading"
      element-loading-text="任务加载中..."
      element-loading-spinner="el-icon-loading"
    >
      <aside class="task-plan-sidebar">
        <div class="sidebar-title">培训计划</div>
        <el-empty
          v-if="!loading && planTaskGroups.length === 0"
          description="当前暂无待完成任务"
          :image-size="90"
        ></el-empty>
        <div v-else class="plan-group-list">
          <div
            v-for="group in planTaskGroups"
            :key="group.planId"
            :class="['plan-group-item', { active: activePlanId === group.planId }]"
            @click="selectPlan(group.planId)"
          >
            <div class="plan-group-name">{{ group.planName }}</div>
            <div class="plan-group-meta">
              <span>待完成 {{ group.taskCount }}</span>
              <span>已解锁 {{ group.unlockedCount }}</span>
            </div>
            <div class="plan-group-tags">
              <el-tag size="mini" type="warning">锁定 {{ group.lockedCount }}</el-tag>
              <el-tag
                v-if="group.planDeadlineTime"
                size="mini"
                :type="group.planOverdueFlag === '1' ? 'danger' : 'info'"
              >
                {{ group.planOverdueFlag === '1' ? '已逾期' : group.planDeadlineTime }}
              </el-tag>
              <el-tag v-if="group.planId === activePlanId" size="mini" type="primary">当前</el-tag>
            </div>
          </div>
        </div>
      </aside>

      <main class="task-detail-section">
        <el-empty
          v-if="!loading && !activePlanGroup"
          description="请选择培训计划"
          :image-size="110"
        ></el-empty>

        <template v-else-if="activePlanGroup">
          <div class="task-detail-header">
            <div>
              <h3 class="task-detail-title">{{ activePlanGroup.planName }}</h3>
              <p class="task-detail-subtitle">按培训计划查看当前待完成资料与考试，避免多计划任务混淆。</p>
            </div>
            <div class="task-detail-metrics">
              <div class="task-metric-card">
                <div class="task-metric-label">待完成</div>
                <div class="task-metric-value">{{ activePlanGroup.taskCount }}</div>
              </div>
              <div class="task-metric-card">
                <div class="task-metric-label">已解锁</div>
                <div class="task-metric-value">{{ activePlanGroup.unlockedCount }}</div>
              </div>
              <div class="task-metric-card">
                <div class="task-metric-label">待解锁</div>
                <div class="task-metric-value">{{ activePlanGroup.lockedCount }}</div>
              </div>
              <div class="task-metric-card">
                <div class="task-metric-label">截止时间</div>
                <div class="task-metric-value">{{ activePlanGroup.planDeadlineTime || '不限时' }}</div>
              </div>
            </div>
          </div>

          <div v-if="activePlanTasks.length === 0" class="task-list-section">
            <el-empty description="该培训计划暂无待完成任务" :image-size="100"></el-empty>
          </div>

          <div v-else class="task-list-section">
            <div class="task-list">
              <div
                v-for="task in activePlanTasks"
                :key="task.id"
                class="task-card"
                :class="{ highlighted: task.id === highlightedTaskId }"
                :data-task-id="task.id"
              >
                <div class="task-main">
                  <div class="task-row top">
                    <div class="task-title">{{ task.title }}</div>
                    <div class="task-tags">
                      <el-tag size="mini" :type="task.taskType === 'exam' ? 'warning' : 'primary'">
                        {{ task.taskType === 'exam' ? '考试任务' : '资料任务' }}
                      </el-tag>
                      <el-tag size="mini" :type="isTaskLocked(task) ? 'info' : 'success'">
                        {{ isTaskLocked(task) ? '未解锁' : '已解锁' }}
                      </el-tag>
                      <el-tag v-if="task.planOverdueFlag === '1'" size="mini" type="danger">
                        已逾期
                      </el-tag>
                      <el-tag v-if="task.id === highlightedTaskId" size="mini" type="danger">
                        新消息
                      </el-tag>
                    </div>
                  </div>

                  <div class="task-row meta">
                    <span>阶段：第{{ task.stageOrder || 0 }}阶段 - {{ task.stageName }}</span>
                  </div>

                  <div v-if="task.planDeadlineTime" class="task-row extra">
                    <span>截止时间：{{ task.planDeadlineTime }}</span>
                  </div>

                  <div v-if="task.taskType === 'exam'" class="task-row extra">
                    <span>及格分：{{ task.passScore }}</span>
                    <span>当前得分：{{ task.userExamScoreDisplay }}</span>
                  </div>

                  <div v-if="task.taskType === 'exam' && task.hasExamAttempt" class="task-row extra">
                    <span>阅卷状态：{{ task.reviewStatusDisplay }}</span>
                  </div>

                  <div v-if="task.taskType === 'material'" class="task-row extra">
                    <span>资料类型：{{ task.materialType || '-' }}</span>
                    <span v-if="task.requiredFlag !== '0'" class="required-text">必学</span>
                  </div>

                  <div v-if="isTaskLocked(task)" class="task-lock-tip">
                    <i class="el-icon-lock"></i>
                    <span>{{ task.unlockTip }}</span>
                  </div>
                </div>

                <div class="task-action">
                  <el-tooltip
                    effect="dark"
                    placement="top"
                    :disabled="!getTaskDisabledTip(task)"
                    :content="getTaskDisabledTip(task)"
                  >
                    <span>
                      <el-button
                        size="mini"
                        type="primary"
                        :disabled="isTaskActionDisabled(task)"
                        @click="goCompleteTask(task)"
                      >
                        {{ getTaskActionText(task) }}
                      </el-button>
                    </span>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </div>
        </template>
      </main>
    </section>
  </div>
</template>

<script>
import { getMyTrainingPlanDetail, getMyTrainingPlanList, readUserNotice } from '@/api/api'

const ONE_DAY_MS = 24 * 60 * 60 * 1000

export default {
  name: 'CenterTasks',
  data() {
    return {
      loading: false,
      taskList: [],
      activePlanId: '',
      highlightedTaskId: '',
      lastHandledNoticeId: ''
    }
  },
  computed: {
    unlockedTaskCount() {
      return this.taskList.filter(item => !this.isTaskLocked(item)).length
    },
    lockedTaskCount() {
      return this.taskList.filter(item => this.isTaskLocked(item)).length
    },
    planTaskGroups() {
      const map = new Map()
      this.taskList.forEach(task => {
        const planId = String(task.planId || '')
        if (!planId) {
          return
        }
        if (!map.has(planId)) {
          map.set(planId, {
            planId,
            planName: task.planName || '培训计划',
            planDeadlineTime: task.planDeadlineTime || '',
            planOverdueFlag: task.planOverdueFlag || '0',
            planUserStatus: task.planUserStatus || '0',
            tasks: [],
            taskCount: 0,
            unlockedCount: 0,
            lockedCount: 0,
            minStageOrder: Number(task.stageOrder || 0)
          })
        }
        const group = map.get(planId)
        group.tasks.push(task)
        group.taskCount += 1
        if (this.isTaskLocked(task)) {
          group.lockedCount += 1
        } else {
          group.unlockedCount += 1
        }
        group.minStageOrder = Math.min(group.minStageOrder, Number(task.stageOrder || 0))
      })
      return Array.from(map.values()).sort((a, b) => {
        const unlockDiff = Number(b.unlockedCount || 0) - Number(a.unlockedCount || 0)
        if (unlockDiff !== 0) {
          return unlockDiff
        }
        const stageDiff = Number(a.minStageOrder || 0) - Number(b.minStageOrder || 0)
        if (stageDiff !== 0) {
          return stageDiff
        }
        return String(a.planName || '').localeCompare(String(b.planName || ''), 'zh')
      })
    },
    activePlanGroup() {
      if (!this.planTaskGroups.length) {
        return null
      }
      return this.planTaskGroups.find(item => item.planId === this.activePlanId) || this.planTaskGroups[0]
    },
    activePlanTasks() {
      return this.activePlanGroup ? this.activePlanGroup.tasks : []
    }
  },
  mounted() {
    this.loadTasks()
  },
  watch: {
    '$route.query': {
      immediate: true,
      handler() {
        this.handleRouteNotice()
      }
    }
  },
  methods: {
    async handleRouteNotice() {
      const query = this.$route && this.$route.query ? this.$route.query : {}
      const noticeId = query.noticeId ? String(query.noticeId) : ''
      if (noticeId && noticeId !== this.lastHandledNoticeId) {
        await this.markNoticeRead(noticeId)
        this.lastHandledNoticeId = noticeId
      }
      this.applyHighlightFromRoute()
    },
    async markNoticeRead(noticeId) {
      if (!noticeId) {
        return
      }
      try {
        await readUserNotice({ id: noticeId })
      } catch (error) {
        // 兜底容错：消息可能已经在其它页面标记已读
      }
    },
    async loadTasks() {
      this.loading = true
      try {
        const planRes = await getMyTrainingPlanList()
        if (planRes.code !== 1000) {
          this.taskList = []
          this.activePlanId = ''
          this.$message.error(planRes.message || '获取任务失败')
          return
        }
        const planList = planRes.data || []
        if (planList.length === 0) {
          this.taskList = []
          this.activePlanId = ''
          return
        }

        const detailResponses = await Promise.all(
          planList.map(plan =>
            getMyTrainingPlanDetail({ planId: plan.id }).catch(() => null)
          )
        )

        const list = []
        detailResponses.forEach((res, index) => {
          if (!res || res.code !== 1000 || !res.data) {
            return
          }
          const detail = res.data
          const plan = planList[index] || {}
          const stageList = Array.isArray(detail.stageList) ? detail.stageList : []
          stageList.forEach(stage => {
            const materialTasks = this.extractStageMaterialTasks(detail, plan, stage)
            list.push(...materialTasks)
            const examTask = this.extractStageExamTask(detail, plan, stage)
            if (examTask) {
              list.push(examTask)
            }
          })
        })

        this.taskList = this.sortTasks(list)
        this.ensureActivePlan()
        this.$nextTick(() => {
          this.applyHighlightFromRoute()
        })
      } catch (error) {
        console.error('loadTasks error:', error)
        this.taskList = []
        this.activePlanId = ''
        this.$message.error('获取任务失败')
      } finally {
        this.loading = false
      }
    },
    ensureActivePlan() {
      if (!this.planTaskGroups.length) {
        this.activePlanId = ''
        return
      }
      const exists = this.planTaskGroups.some(item => item.planId === this.activePlanId)
      if (!exists) {
        this.activePlanId = this.planTaskGroups[0].planId
      }
    },
    selectPlan(planId) {
      this.activePlanId = String(planId || '')
      this.$nextTick(() => {
        if (this.highlightedTaskId) {
          this.scrollToTask(this.highlightedTaskId)
        }
      })
    },
    extractStageMaterialTasks(detail, plan, stage) {
      const materialList = Array.isArray(stage.materialList) ? stage.materialList : []
      if (materialList.length === 0) {
        return []
      }
      const hasExplicitRequired = materialList.some(rel => rel && rel.requiredFlag !== '0')
      const result = []
      materialList.forEach(rel => {
        if (!rel) {
          return
        }
        const isRequired = hasExplicitRequired ? rel.requiredFlag !== '0' : true
        if (!isRequired) {
          return
        }
        const material = rel.material || {}
        const materialId = rel.materialId || material.id || ''
        if (!materialId) {
          return
        }
        result.push({
          id: `${detail.id || plan.id}:${stage.id}:material:${materialId}`,
          taskType: 'material',
          planId: detail.id || plan.id,
          planName: detail.planName || plan.planName || '培训计划',
          stageId: stage.id || '',
          stageName: stage.stageName || '',
          stageOrder: stage.stageOrder || 0,
          stageStatus: stage.stageStatus || '0',
          taskStatus: rel.userStatus === '1' ? 'completed' : 'pending',
          unlockTip: this.buildUnlockTip(detail.planStartTime, stage),
          title: material.title || '未命名资料',
          videoId: material.videoId ? String(material.videoId) : '',
          materialId,
          materialType: (material.fileType || '').toLowerCase(),
          materialFileUrl: material.fileUrl || '',
          requiredFlag: rel.requiredFlag,
          planDeadlineTime: detail.planDeadlineTime || plan.planDeadlineTime || '',
          planOverdueFlag: detail.planOverdueFlag || plan.planOverdueFlag || '0',
          planUserStatus: detail.planUserStatus || plan.planUserStatus || '0'
        })
      })
      return result
    },
    extractStageExamTask(detail, plan, stage) {
      if (!stage || !stage.examId) {
        return null
      }
      if (!this.stageHasExam(stage)) {
        return null
      }
      if (stage.requiredPass === '0') {
        return null
      }
      const passScore = Number(stage.passScore || 60)
      const currentScore = this.toNumberOrNull(stage.userExamScore)
      const hasExamAttempt = this.hasStageExamAttempt(stage)
      const finalScoreReady = this.isStageExamFinalScoreReady(stage)
      const passed = finalScoreReady && currentScore !== null && currentScore >= passScore
      return {
        id: `${detail.id || plan.id}:${stage.id}:exam:${stage.examId}`,
        taskType: 'exam',
        planId: detail.id || plan.id,
        planName: detail.planName || plan.planName || '培训计划',
        stageId: stage.id || '',
        stageName: stage.stageName || '',
        stageOrder: stage.stageOrder || 0,
        stageStatus: stage.stageStatus || '0',
          taskStatus: passed ? 'completed' : 'pending',
        unlockTip: this.buildUnlockTip(detail.planStartTime, stage),
        title: `阶段考试：${stage.examName || '未命名考试'}`,
        examId: stage.examId,
        hasExamAttempt,
        passScore,
        finalScoreReady,
        reviewStatus: stage.examReviewStatus || (hasExamAttempt ? '无需阅卷' : '未提交'),
        pendingReviewCount: Number(stage.examPendingReviewCount || 0),
        userExamScoreDisplay: this.formatStageExamScoreDisplay(stage),
        reviewStatusDisplay: this.formatStageExamReviewStatus(stage),
        planDeadlineTime: detail.planDeadlineTime || plan.planDeadlineTime || '',
        planOverdueFlag: detail.planOverdueFlag || plan.planOverdueFlag || '0',
        planUserStatus: detail.planUserStatus || plan.planUserStatus || '0'
      }
    },
    stageHasExam(stage) {
      const stageType = stage.stageType || ''
      return stageType === 'resource_exam' || stageType === 'exam_only'
    },
    sortTasks(tasks) {
      const list = Array.isArray(tasks) ? tasks.slice() : []
      const statusWeight = item => {
        if (item.stageStatus === '1') return 0
        if (item.stageStatus === '0') return 1
        return 2
      }
      list.sort((a, b) => {
        const statusDiff = statusWeight(a) - statusWeight(b)
        if (statusDiff !== 0) {
          return statusDiff
        }
        const planDiff = String(a.planName || '').localeCompare(String(b.planName || ''), 'zh')
        if (planDiff !== 0) {
          return planDiff
        }
        const stageDiff = Number(a.stageOrder || 0) - Number(b.stageOrder || 0)
        if (stageDiff !== 0) {
          return stageDiff
        }
        return String(a.title || '').localeCompare(String(b.title || ''), 'zh')
      })
      return list
    },
    applyHighlightFromRoute() {
      if (!Array.isArray(this.taskList) || this.taskList.length === 0) {
        this.highlightedTaskId = ''
        return
      }
      const targetId = this.findHighlightTaskId()
      this.highlightedTaskId = targetId
      if (!targetId) {
        this.ensureActivePlan()
        return
      }
      const targetTask = this.taskList.find(item => item.id === targetId)
      if (targetTask && targetTask.planId) {
        this.activePlanId = String(targetTask.planId)
      }
      this.$nextTick(() => {
        this.scrollToTask(targetId)
      })
    },
    findHighlightTaskId() {
      const query = this.$route && this.$route.query ? this.$route.query : {}
      if (!query || Object.keys(query).length === 0) {
        return ''
      }
      const expectTaskId = query.taskId ? String(query.taskId) : ''
      if (expectTaskId && this.taskList.some(item => item.id === expectTaskId)) {
        return expectTaskId
      }
      let candidates = this.taskList.slice()
      const planId = query.planId ? String(query.planId) : ''
      const stageId = query.stageId ? String(query.stageId) : ''
      const materialId = query.materialId ? String(query.materialId) : ''
      const examId = query.examId ? String(query.examId) : ''
      const taskType = query.taskType ? String(query.taskType) : ''
      if (planId) {
        candidates = candidates.filter(item => String(item.planId || '') === planId)
      }
      if (stageId) {
        candidates = candidates.filter(item => String(item.stageId || '') === stageId)
      }
      if (taskType && taskType !== 'plan') {
        candidates = candidates.filter(item => String(item.taskType || '') === taskType)
      }
      if (materialId) {
        candidates = candidates.filter(item => String(item.materialId || '') === materialId)
      }
      if (examId) {
        candidates = candidates.filter(item => String(item.examId || '') === examId)
      }
      if (candidates.length === 0) {
        return ''
      }
      candidates.sort((a, b) => {
        const unlockDiff = Number(this.isTaskLocked(a)) - Number(this.isTaskLocked(b))
        if (unlockDiff !== 0) {
          return unlockDiff
        }
        const stageDiff = Number(a.stageOrder || 0) - Number(b.stageOrder || 0)
        if (stageDiff !== 0) {
          return stageDiff
        }
        return String(a.title || '').localeCompare(String(b.title || ''), 'zh')
      })
      return candidates[0].id || ''
    },
    scrollToTask(taskId) {
      if (!this.$el || !taskId) {
        return
      }
      const cards = this.$el.querySelectorAll('.task-card')
      const target = Array.from(cards).find(item => item.getAttribute('data-task-id') === taskId)
      if (!target) {
        return
      }
      target.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      })
    },
    isTaskLocked(task) {
      return !task || task.stageStatus === '0'
    },
    isTaskExpiredForAction(task) {
      if (!task || task.planOverdueFlag !== '1' || task.planUserStatus === '1') {
        return false
      }
      if (task.taskType === 'exam') {
        return !task.hasExamAttempt
      }
      return task.taskStatus !== 'completed'
    },
    isTaskActionDisabled(task) {
      return this.isTaskLocked(task) || this.isTaskExpiredForAction(task)
    },
    getTaskDisabledTip(task) {
      if (this.isTaskLocked(task)) {
        return task.unlockTip || '阶段未解锁，解锁条件达标后可继续'
      }
      if (this.isTaskExpiredForAction(task)) {
        return this.getTaskExpiredTip(task)
      }
      return ''
    },
    getTaskExpiredTip(task) {
      if (task && task.planDeadlineTime) {
        return `培训计划已于${task.planDeadlineTime}截止，当前未完成项不可继续完成`
      }
      return '培训计划已逾期，当前未完成项不可继续完成'
    },
    buildUnlockTip(planStartTime, stage) {
      const reason = (stage && stage.lockReason) || '阶段未解锁，解锁条件达标后可继续'
      if (!stage || stage.stageStatus !== '0') {
        return ''
      }
      const unlockDateText = this.getUnlockDateText(planStartTime, stage.unlockDay)
      if (!unlockDateText) {
        return reason
      }
      if (String(reason).includes(unlockDateText)) {
        return reason
      }
      return `${reason}；${unlockDateText}`
    },
    getUnlockDateText(planStartTime, unlockDay) {
      const startDate = this.parseDate(planStartTime)
      const day = Number(unlockDay || 0)
      if (!startDate || !day || day < 1) {
        return ''
      }
      const unlockDate = new Date(startDate.getTime() + (day - 1) * ONE_DAY_MS)
      return `预计开放时间：${this.formatDateTime(unlockDate)}`
    },
    parseDate(value) {
      if (!value) {
        return null
      }
      const normalized = String(value).replace(/-/g, '/')
      const date = new Date(normalized)
      if (Number.isNaN(date.getTime())) {
        return null
      }
      return date
    },
    formatDateTime(date) {
      const d = date instanceof Date ? date : this.parseDate(date)
      if (!d) {
        return '-'
      }
      const yyyy = d.getFullYear()
      const mm = String(d.getMonth() + 1).padStart(2, '0')
      const dd = String(d.getDate()).padStart(2, '0')
      const hh = String(d.getHours()).padStart(2, '0')
      const mi = String(d.getMinutes()).padStart(2, '0')
      return `${yyyy}-${mm}-${dd} ${hh}:${mi}`
    },
    toNumberOrNull(value) {
      if (value === null || value === undefined) {
        return null
      }
      if (typeof value === 'string' && value.trim() === '') {
        return null
      }
      const num = Number(value)
      if (Number.isNaN(num)) {
        return null
      }
      return num
    },
    hasStageExamAttempt(stage) {
      if (!stage) {
        return false
      }
      if (stage.examSubmitted === true) {
        return true
      }
      return this.toNumberOrNull(stage.userExamScore) !== null
    },
    isStageExamFinalScoreReady(stage) {
      return !stage || stage.examFinalScoreReady !== false
    },
    isStageExamPendingReview(stage) {
      return this.hasStageExamAttempt(stage) && !this.isStageExamFinalScoreReady(stage)
    },
    isStageExamPassed(stage) {
      const currentScore = this.toNumberOrNull(stage && stage.userExamScore)
      const passScore = Number((stage && stage.passScore) || 60)
      return this.isStageExamFinalScoreReady(stage) && currentScore !== null && currentScore >= passScore
    },
    formatStageExamScoreDisplay(stage) {
      const currentScore = this.toNumberOrNull(stage && stage.userExamScore)
      if (!this.hasStageExamAttempt(stage) || currentScore === null) {
        return '暂无'
      }
      if (this.isStageExamPendingReview(stage)) {
        return `${currentScore} 分（临时）`
      }
      return `${currentScore} 分`
    },
    formatStageExamReviewStatus(stage) {
      if (!this.hasStageExamAttempt(stage)) {
        return '未提交'
      }
      if (this.isStageExamPendingReview(stage)) {
        return `待管理员阅卷（剩余 ${Number(stage && stage.examPendingReviewCount ? stage.examPendingReviewCount : 0)} 题）`
      }
      return (stage && stage.examReviewStatus) || '无需阅卷'
    },
    hasExamAttempt(task) {
      return !!task && task.taskType === 'exam' && task.hasExamAttempt === true
    },
    getMaterialExt(task) {
      const ext = String(task.materialType || '').toLowerCase()
      if (ext) {
        return ext
      }
      const fileUrl = String(task.materialFileUrl || '')
      const clean = fileUrl.split('?')[0]
      const idx = clean.lastIndexOf('.')
      return idx >= 0 ? clean.slice(idx + 1).toLowerCase() : ''
    },
    getTaskActionText(task) {
      if (!task) {
        return '去完成'
      }
      if (this.isTaskExpiredForAction(task)) {
        return '已逾期'
      }
      if (task.taskType === 'exam') {
        if (this.hasExamAttempt(task)) {
          return '查看结果'
        }
        return '开始考试'
      }
      if (task.taskStatus === 'completed') {
        return '查看资料'
      }
      return '去完成'
    },
    isVideoExt(ext) {
      return ['mp4', 'mov', 'm3u8', 'avi', 'wmv', 'flv', 'mkv', 'webm'].includes(ext)
    },
    goCompleteTask(task) {
      if (this.isTaskActionDisabled(task)) {
        this.$message.warning(this.getTaskDisabledTip(task))
        return
      }
      if (task.taskType === 'exam') {
        if (!task.examId) {
          this.$message.warning('考试信息不完整')
          return
        }
        if (this.hasExamAttempt(task)) {
          this.$router.push({
            path: '/testResult',
            query: {
              id: task.examId,
              fromTask: '1',
              passScore: task.passScore || 60
            }
          })
          return
        }
        this.$router.push({
          path: '/testInfo',
          query: {
            id: task.examId,
            planId: task.planId,
            stageId: task.stageId,
            taskType: task.taskType,
            fromTask: '1',
            passScore: task.passScore || 60
          }
        })
        return
      }
      if (task.taskType === 'material') {
        if (!task.materialFileUrl) {
          this.$message.warning('资料文件不存在')
          return
        }
        const ext = this.getMaterialExt(task)
        if (this.isVideoExt(ext)) {
          this.$router.push({
            path: '/videoPlay',
            query: {
              id: task.videoId || '',
              from: 'training',
              fileUrl: task.materialFileUrl || '',
              title: task.title || '培训资料',
              planId: task.planId,
              stageId: task.stageId,
              materialId: task.materialId
            }
          })
          return
        }
        this.$router.push({
          path: '/trainingPlanGuide',
          query: {
            planId: task.planId,
            stageId: task.stageId,
            materialId: task.materialId,
            autoOpen: '1'
          }
        })
      }
    }
  }
}
</script>

<style scoped>
.tasks-page {
  min-height: 100%;
  padding: 2px;
}

.task-layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 16px;
  align-items: start;
}

.task-plan-sidebar {
  background: #fff;
  border: 1px solid #e6edf6;
  border-radius: 12px;
  padding: 14px;
  position: sticky;
  top: 12px;
}

.sidebar-title {
  font-size: 16px;
  font-weight: 700;
  color: #1f2d3d;
  margin-bottom: 10px;
}

.plan-group-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.plan-group-item {
  border: 1px solid #e7ecf2;
  border-radius: 10px;
  padding: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
}

.plan-group-item:hover {
  border-color: #409eff;
}

.plan-group-item.active {
  border-color: #409eff;
  background: #ecf5ff;
}

.plan-group-name {
  font-size: 14px;
  font-weight: 700;
  color: #203040;
}

.plan-group-meta {
  margin-top: 6px;
  display: flex;
  justify-content: space-between;
  gap: 8px;
  color: #7a8b9a;
  font-size: 12px;
}

.plan-group-tags {
  margin-top: 8px;
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.task-detail-section {
  min-width: 0;
}

.task-detail-header {
  background: #fff;
  border: 1px solid #e6edf6;
  border-radius: 12px;
  padding: 16px 18px;
  display: flex;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.task-detail-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: #1e2f43;
}

.task-detail-subtitle {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 13px;
}

.task-detail-metrics {
  display: flex;
  gap: 10px;
}

.task-metric-card {
  min-width: 110px;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #edf2f7;
  padding: 10px 12px;
}

.task-metric-label {
  color: #6b7c93;
  font-size: 12px;
}

.task-metric-value {
  margin-top: 6px;
  color: #1f2d3d;
  font-size: 22px;
  font-weight: 700;
}

.tasks-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 14px;
}

.page-title {
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: #1e2f43;
}

.page-subtitle {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 13px;
}

.task-stats {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin-bottom: 14px;
}

.stat-item {
  border-radius: 10px;
  border: 1px solid #e6edf6;
  background: #ffffff;
  padding: 12px 14px;
}

.stat-item.actionable {
  border-color: #d5e6ff;
  background: #f5f9ff;
}

.stat-item.locked {
  border-color: #ffe4bd;
  background: #fff9ef;
}

.stat-item.total {
  border-color: #e9edf2;
  background: #f8fafc;
}

.stat-label {
  color: #6b7c93;
  font-size: 12px;
}

.stat-value {
  margin-top: 4px;
  color: #1f2d3d;
  font-size: 24px;
  font-weight: 700;
}

.task-list-section {
  background: #fff;
  border: 1px solid #e6edf6;
  border-radius: 12px;
  padding: 14px;
  min-height: 300px;
}

.task-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.task-card {
  border: 1px solid #edf2f7;
  border-radius: 10px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  gap: 12px;
  transition: all 0.2s ease;
}

.task-card.highlighted {
  border-color: #f59e0b;
  background: #fff9ef;
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.2);
}

.task-main {
  flex: 1;
  min-width: 0;
}

.task-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.task-row.top {
  justify-content: space-between;
}

.task-title {
  color: #1e293b;
  font-size: 15px;
  font-weight: 700;
}

.task-tags {
  display: flex;
  gap: 6px;
}

.task-row.meta,
.task-row.extra {
  margin-top: 8px;
  color: #64748b;
  font-size: 12px;
}

.required-text {
  color: #ef4444;
  font-weight: 600;
}

.task-lock-tip {
  margin-top: 8px;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid #ffe3c1;
  background: #fff7eb;
  color: #b45309;
  font-size: 12px;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.task-action {
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 86px;
}

@media (max-width: 1200px) {
  .task-stats {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .task-layout {
    grid-template-columns: 260px 1fr;
  }

  .task-detail-header {
    flex-direction: column;
  }

  .task-detail-metrics {
    flex-wrap: wrap;
  }
}

@media (max-width: 768px) {
  .tasks-header {
    flex-direction: column;
    align-items: stretch;
  }

  .task-stats {
    grid-template-columns: 1fr;
  }

  .task-layout {
    grid-template-columns: 1fr;
  }

  .task-plan-sidebar {
    position: static;
  }

  .task-card {
    flex-direction: column;
  }

  .task-action {
    justify-content: flex-start;
  }
}
</style>
